import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { apiClient } from "../../lib/apiClient";
import { cn } from "../../lib/utils";

export function SystemHealthIndicator() {
  const [health, setHealth] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;
    apiClient
      .get("/system/health")
      .then(({ data }) => mounted && setHealth(data))
      .catch(() => mounted && setLoadError(true));
    return () => {
      mounted = false;
    };
  }, []);

  const level = loadError ? "red" : health ? (health.status === "healthy" ? "green" : "yellow") : "gray";
  const dotClass = { green: "bg-success", yellow: "bg-warning", red: "bg-danger", gray: "bg-deactivated" }[level];
  const orphanTotal = health ? Object.values(health.orphans).reduce((a, b) => a + b, 0) : 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="system-health-indicator"
          className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-main transition-colors hover:bg-background"
        >
          <span className={cn("relative flex h-2.5 w-2.5 rounded-full", dotClass)}>
            {level === "green" && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            )}
          </span>
          System Health
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="system-health-popover">
        <div className="flex items-center gap-2 border-b border-border pb-3">
          {level === "green" && <CheckCircle2 size={18} className="text-success" strokeWidth={1.5} />}
          {level === "yellow" && <AlertTriangle size={18} className="text-warning" strokeWidth={1.5} />}
          {(level === "red" || level === "gray") && <XCircle size={18} className="text-danger" strokeWidth={1.5} />}
          <p className="font-heading text-sm font-semibold text-text-main">
            {loadError ? "Health check unavailable" : health ? `System ${health.status}` : "Checking..."}
          </p>
        </div>
        {health && (
          <div className="mt-3 space-y-2 text-xs">
            <Row
              label="Database"
              value={health.db.connected ? `Connected (${health.db.latency_ms}ms)` : "Unreachable"}
              ok={health.db.connected}
            />
            <Row
              label="Payment reconciliation"
              value={`${health.payments.amount_mismatches} mismatch(es), ${health.payments.without_payment} unpaid`}
              ok={health.payments.amount_mismatches === 0}
            />
            <Row
              label="Route counters"
              value={`${health.routes.total_mismatches + health.routes.done_mismatches} drift`}
              ok={health.routes.total_mismatches === 0 && health.routes.done_mismatches === 0}
            />
            <Row label="Orphan records" value={`${orphanTotal} found`} ok={orphanTotal === 0} />
            {health.issues.length > 0 && (
              <ul className="mt-2 space-y-1 border-t border-border pt-2 text-text-muted" data-testid="system-health-issues">
                {health.issues.map((issue, i) => (
                  <li key={i}>• {issue}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {loadError && (
          <p className="mt-2 text-xs text-text-muted">Could not reach /api/v2/system/health.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, value, ok }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-text-muted">{label}</span>
      <span className={cn("font-medium", ok ? "text-success" : "text-danger")}>{value}</span>
    </div>
  );
}
